import {
	Dialog,
	Form,
	FormattedText,
	FormItem,
	TextInput,
	Toolbar,
	Typography,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import { inline, isNotEmpty } from "client/Validation/rules";
import {
	UpdateIntegrationCredentialsPasswordSaveModel,
	updatePassword
} from "client/Api/IntegrationCredentials/integrationCredentials";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

type UpdatePasswordDialogProps = {
	credentials: { id: number; username: string; };
	onAfterUpdate: () => void;
	onClose: () => void;
};

const UpdatePasswordDialog = (props: UpdatePasswordDialogProps): ReactElement => {
	const {
		credentials,
		onAfterUpdate,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<UpdateIntegrationCredentialsPasswordSaveModel>({
		password: "",
		confirmPassword: ""
	});

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManger: AlertManager = useAlerts();

	const formHandler: IFormHandler<UpdateIntegrationCredentialsPasswordSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<UpdateIntegrationCredentialsPasswordSaveModel> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await updatePassword(
					credentials.id,
					formData,
					globalCancellation
				);

				onAfterUpdate();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManger.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManger,
			credentials.id,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onAfterUpdate
		]
	);

	return (
		<Dialog
			trackingId="integration-credentials-update-password-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_UpdatePassword")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<p>
					<FormattedText
						text={localize("_PleaseEnterTheMostRecentPasswordOfTheCredentialsUsername")}
						thingsToReplace={[
							{
								what: "{{Username}}",
								filler: (
									<Typography weight="bold">
										{credentials.username}
									</Typography>
								)
							}
						]}
					/>
				</p>

				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Password")} isRequired>
						<TextInput
							{...formHandler.for("password").attachToTextInput()}
							type="password"
							autoFocus="focus"
						/>

						<ValidationError {...formHandler.for("password").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ConfirmPassword")} isRequired>
						<TextInput
							{...formHandler.for("confirmPassword").attachToTextInput()}
							type="password"
						/>

						<ValidationError {...formHandler.for("confirmPassword").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						trackingId="save"
						text={localize("_Save")}
						onClick={submitHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
UpdatePasswordDialog.displayName = "UpdatePasswordDialog";

const validationConfiguration: IValidationConfiguration<UpdateIntegrationCredentialsPasswordSaveModel> =
	createValidationConfiguration<UpdateIntegrationCredentialsPasswordSaveModel>();

validationConfiguration.forScalar("password")
	.add(isNotEmpty());

validationConfiguration.forScalar("confirmPassword")
	.add(
		isNotEmpty(),
		inline(
			(value, context): InputValidationResult => {
				const model = context.parentValue as UpdateIntegrationCredentialsPasswordSaveModel;
				if (value !== model.password) {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_ValidationPasswordConfirm")
					};
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
	);

export type UpdatePasswordDialogInitialData = Omit<
	UpdatePasswordDialogProps,
	"onClose" | "onAfterUpdate"
>;

export function useUpdatePasswordDialog(): [
	element: ReactElement | null,
	open: (props: UpdatePasswordDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<UpdatePasswordDialog
				{...props}
				onClose={onClose}
				onAfterUpdate={onSave}
			/>
		)
	);
}
